import React, { Fragment, useEffect, useState } from 'react'

import { LoadingSpinner, BarChartComponent, PieChartComponent, MetricLineChart } from '../components'
import { TrainingsOverviewMetaProps, CategoryType, Training, Event, Area, Shift } from '../data/types'
import { VolunteerViewModal } from '../components/mangement_information/VolunteerViewModal'

import { API_BASE_URL } from '../data'

type Props = {
    meta: TrainingsOverviewMetaProps,
    training?: Training | null,
    event?: Event | null,
    area?: Area | null,
    shift?: Shift | null
}

export const TrainingsOverview = ({ meta, training, event, area, shift }: Props) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [groupedData, setGroupData] = useState<{ name: string, count: number }[] | null>(null)
    const [isVolunteerViewModalOpen, setIsVolunteerViewModalOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<CategoryType | null>(null);

    const buildQueryString = () => {
        const params = new URLSearchParams()
        params.append('country', 'eu');
        if (training) params.append('training_id', training.id.toString());
        if (shift) params.append('shift_id', shift.id.toString());
        else if (area) params.append('area_id', area.id.toString());
        else if (event) params.append('event_id', event.id.toString());
        return params.toString();
    }

    useEffect(() => {

        const fetchGroupStats = async () => {
            setLoading(true);
            setError(null)
            try {
                const queryString = buildQueryString();
                const response = await fetch(`${API_BASE_URL}/dashboard/stats/training?${queryString}`, { credentials: 'include' })
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`)
                }
                const result = await response.json()
                setGroupData(result);
            } catch (err) {
                setError(err instanceof Error ? err.message : "Error while fetching stats.")
            }
            finally {
                setLoading(false);
            }
        }
        fetchGroupStats();

    }, [training, event, area, shift])

    const handleCategoryClick = (category: CategoryType) => {
        setIsVolunteerViewModalOpen(true)
        setSelectedCategory(category);
    }

    const handleCategoryClickClose = () => {
        setSelectedCategory(null);
        setIsVolunteerViewModalOpen(false);
    }

    return (
        <Fragment>
            <div className="flex-1 container mx-auto">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                        {meta.title}
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400">
                        {meta.description}
                    </p>
                </div>

                {loading && <LoadingSpinner meta={{ loadingText: meta.loadingText }} />}

                {error && (
                    <div
                        className="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-danger-800 dark:text-danger-200 mb-2">
                            {meta.errorTitle}
                        </h3>
                        <p className="text-danger-600 dark:text-danger-300">{error}</p>
                    </div>
                )}

                {!loading && !error && (
                    <div className="space-y-8">

                        {/* Trainings Completed Metric Chart - Only show when no specific training is selected */}
                        {!training && (
                            <MetricLineChart
                                metricKey="trainings_completed"
                                title="Training Completions/Policy Acknowledgements"
                                description="Cumulative sum of trainings completed or policies acknowledged by volunteers"
                                days={3}
                                aggregation="none"
                                lineColor="#10b981"
                                height={220}
                            />
                        )}

                        {!training ?
                            (
                                <BarChartComponent
                                    title="Completions per Training"
                                    data={groupedData}
                                    groupBy="preference"
                                    height="xl"
                                    width="8xl"
                                    onChartClick={handleCategoryClick}
                                />
                            )
                            : (
                                <PieChartComponent
                                    title="Completion Status Distribution"
                                    data={groupedData}
                                    groupBy="language"
                                    width="5xl"
                                    onChartClick={handleCategoryClick}
                                />
                            )}
                    </div>
                )}
            </div>
            <VolunteerViewModal
                title={`Volunteers for ${selectedCategory?.field} : ${selectedCategory?.value}`}
                isOpen={isVolunteerViewModalOpen}
                onCancel={handleCategoryClickClose}
                field={selectedCategory?.field}
                value={selectedCategory?.value}
                training={training}
                event={event}
                area={area}
                shift={shift}
                forTraining={true}

            />
        </Fragment>
    )
}
